import { useEffect, useRef } from "react";

import { useAnalytics } from "@accurx/analytics";
import { useAddNoteMutation } from "@accurx/concierge/hooks/mutations/useAddNoteMutation";
import { Conversation, PatientSummary } from "@accurx/concierge/types";
import { Ds, Feedback, Text } from "@accurx/design";
import { NoteForm } from "domains/inbox/components/NoteForm/NoteForm";
import { useConversationActionTrackingFields } from "domains/inbox/hooks/util/useConversatonActionAnalytics";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

type ComposeNoteProps = {
    noteText: string;
    setNoteText: (value: string) => void;
    conversation: Conversation;
    closeNotePanel: () => void;
    patient?: PatientSummary;
    setSentNoteStatus: (status: string | null) => void;
    onMinimiseClick?: () => void;
} & ExpandedInfo;

type ExpandedInfo =
    | {
          onExpandClick?: never;
          isHeightRestricted?: boolean;
      }
    | {
          onExpandClick: () => void;
          isHeightRestricted: boolean;
      };

export const ComposeNote = ({
    noteText,
    conversation,
    patient,
    closeNotePanel,
    setSentNoteStatus,
    onMinimiseClick,
    onExpandClick,
    setNoteText,
    isHeightRestricted = false,
}: ComposeNoteProps) => {
    const history = useHistory();
    const currentURL = history.location.pathname + history.location.search;

    const ref = useRef<HTMLTextAreaElement | null>(null);

    const track = useAnalytics();
    const addNoteButtonClickAnalytics =
        useConversationActionTrackingFields(conversation);

    useEffect(() => {
        ref.current?.focus();
    }, []);

    const addNote = useAddNoteMutation({
        onSuccess: () => {
            setSentNoteStatus("Note was successfully saved");
        },
        onError: () => {
            toast(
                <Feedback
                    title={
                        <Ds.Flex
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Text variant="label">
                                An error occurred when trying to send your
                                message
                            </Text>
                            <Ds.Button
                                appearance="tertiary"
                                title="Close alert"
                            >
                                <Ds.Button.Icon
                                    name="Cross"
                                    aria-label="Close alert"
                                />
                            </Ds.Button>
                        </Ds.Flex>
                    }
                    colour="error"
                >
                    <Ds.Flex flexDirection="column">
                        <Text skinny>
                            Go back to your message and try saving your note
                            again. If this keeps happening, please get in touch
                            with our support team.
                        </Text>
                        <Link to={currentURL} style={{ width: "fit-content" }}>
                            <Text skinny colour="blue" as="span">
                                Go to message
                            </Text>
                        </Link>
                    </Ds.Flex>
                </Feedback>,
                { autoClose: false },
            );
        },
        onSettled: (_, error) => {
            closeNotePanel();

            track("ConversationAction Button Response", {
                ...addNoteButtonClickAnalytics({
                    type: "AddNote",
                    appOrigin: "ComposeNote",
                }),
                withNote: true,
                noteLength: noteText.length,
                hasError: !!error,
            });
        },
    });

    const handleSubmit = (note: string) => {
        addNote.mutate({
            conversation,
            patientIds: patient?.externalIds,
            note,
        });

        track("ConversationAction Button Click", {
            ...addNoteButtonClickAnalytics({
                type: "AddNote",
                appOrigin: "ComposeNote",
            }),
            withNote: true,
            noteLength: note.length,
        });
    };

    return (
        <NoteForm
            value={noteText}
            onChange={setNoteText}
            onSubmit={handleSubmit}
            onExpand={onExpandClick}
            onMinimise={onMinimiseClick}
            isLoading={addNote.isLoading}
            isHeightRestricted={isHeightRestricted}
        />
    );
};
